<template>
    <div>
        <vue-headful title="Billing :: MeritEHS"/>

        <h4 class="py-3 mb-3">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-credit-card"></i> Billing</li>
            </ol>
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
            <b-nav-item :active="curTab === 'billing'" @click="curTab = 'billing'">
                Billing Settings
            </b-nav-item>
            <b-nav-item :active="curTab === 'invoices'" @click="curTab = 'invoices'">
                Past Invoices & Charges
            </b-nav-item>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block">
            <b-dropdown :html="getTabTitle" variant="default" class="bg-white">
                <b-dropdown-item :active="curTab === 'billing'" @click="curTab = 'billing'">
                    Billing Settings
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'invoices'" @click="curTab = 'invoices'">
                    Past Invoices & Charges
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div v-show="curTab === 'billing'">
            <div v-if="loading" class="mb-4">
                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
            </div>
            <div class="row mb-3">
                <div class="col-xl-9 col-lg-12 mb-3">
                    <b-card class="mb-4">
                        <label class="text-muted">Card Type:</label> <span v-if="account && account.card_brand">{{ account.card_brand }}</span><br>
                        <label class="text-muted">Current Card:</label> <span v-if="account && account.card_last_four">XXXX-XXXX-XXXX-{{ account.card_last_four }}</span><br>
                        <label class="text-muted">Next Billing Date:</label> <span v-if="account && account.next_bill_date">{{ account.next_bill_date }}</span>
                        <hr/>

                        <h5 class="text-muted">Update Card</h5>
                        <div id="card-element"></div>
                        <div id="card-errors" class="text-danger"></div>
                        <hr/>

                        <h5 class="mt-4" :class="{'text-muted': !errors.billing_email, 'text-danger': errors.billing_email}">Billing Email - MeritEHS Will Send Receipts Here <i class="fa fa-asterisk text-danger"></i></h5>
                        <div class="mb-4">
                            <b-form-input v-model="account.billing_email" placeholder="Enter Billing Email Address" :class="{'is-invalid': errors.billing_email}"></b-form-input>

                            <div v-if="errors.billing_email" class="text-danger mt-2">
                                <span v-for="error in errors.billing_email">{{ error }}</span>
                            </div>
                        </div>

                        <b-button variant="success" size="sm" v-on:click="updateCard()" :disabled="saving">
                            <template v-if="saving">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Updating
                            </template>
                            <template v-else>
                                Update Billing
                            </template>
                        </b-button>
                    </b-card>

                    <b-card class="mb-4" title="User Licenses">
                        <div style="font-size: 16px;"><span class="text-muted">Billing Plan:</span> {{ account.plan_name }}</div>
                        <div class="mb-3">{{ account.plan_description }}</div>

                        <div class="row mt-4 mb-3">
                            <div class="col-md-3 text-md-right">
                                Standard Users <i class="fa fa-question-circle" :title="`$${pricePerUser}/user: Users with at least 1 permission outside of 'Corrective Actions - View Assigned to Them'`"></i>:<br/>
                            </div>
                            <div class="col-md-4">
                                <template v-if="planHasCounts">
                                    <b-form-input type="number" v-model="account.prepaid_nbr_users" placeholder="Enter Number of Standard User Licenses"></b-form-input>
                                    <div v-if="errors.prepaid_nbr_users" class="text-danger mt-2">
                                        <span v-for="error in errors.prepaid_nbr_users">{{ error }}</span>
                                    </div>
                                </template>
                                <span v-else-if="billableUsers">
                                    {{ billableUsers.full_price_users }}
                                </span>
                            </div>
                            <div class="col-md-4">
                                <span class="text-danger">Current Usage: {{ activeUsers.standard }}</span>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-3 text-md-right">
                                Free Users <i class="fa fa-question-circle" title="Free User: Any user without permissions or only with the Corrective Action User permission. This usually applies to subcontractors and user records for reference."></i>:
                            </div>
                            <div class="col-md-4">
                                <div v-if="billableUsers">{{ billableUsers.free_users + activeUsers.action }} </div>
                            </div>
                        </div>

                        <div class="alert alert-warning mt-3" v-if="countsChanged && (proratedCost > 0.50 || calculatingProratedCost)">
                            You will immediately be billed for the prorated difference for new licenses: <i class="fa fa-spin fa-spinner" v-if="calculatingProratedCost"></i><strong v-else>${{ proratedCost }}</strong>
                        </div>

                        <button v-if="planHasCounts" :disabled="!countsChanged || saving" class="btn btn-sm btn-success mt-4" v-on:click="saveLicenses()">Update License Counts</button>

                        <div v-if="errors.general_billing_error" class="text-danger mt-2">
                            <span v-for="error in errors.general_billing_error">{{ error }}</span>
                        </div>
                    </b-card>

                    <b-card class="mb-4" title="Email/Text Sending Credits">
                        <div class="mb-3 text-muted"></div>

                        <div class="row mt-4 mb-3">
                            <div class="col-md-3 text-md-right">
                                Text Credits (${{ cost_per_1000_texts }} per 1000)
                            </div>
                            <div class="col-md-4">
                                <b-form-input type="number" v-model="text_credits" placeholder="Enter Sets of 1,000 to Purchase"></b-form-input>

                                <div class="my-2 text-info">Enter the number of sets of 1,000 texts to purchase. To purchase 2,000 credits, you'd enter 2.</div>

                                <div v-if="errors.text_credits" class="text-danger mt-2">
                                    <span v-for="error in errors.text_credits">{{ error }}</span>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <span class="text-danger">Remaining Credits: {{ account.monthly_text_limit }}</span>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-3 text-md-right">
                                Email Credits (${{ cost_per_1000_emails }} per 1000)
                            </div>
                            <div class="col-md-4">
                                <b-form-input type="number" v-model="email_credits" placeholder="Enter Sets of 1,000 to Purchase"></b-form-input>

                                <div class="my-2 text-info">Enter the number of sets of 1,000 emails to purchase. To purchase 2,000 credits, you'd enter 2.</div>

                                <div v-if="errors.email_credits" class="text-danger mt-2">
                                    <span v-for="error in errors.email_credits">{{ error }}</span>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <span class="text-danger">Remaining Credits: {{ account.monthly_email_limit }}</span>
                            </div>
                        </div>

                        <div class="alert alert-warning mt-3" v-if="text_credits || email_credits">
                            You are about to purchase <span v-if="text_credits">{{ text_credits * 1000 }} Text Credits</span> <span v-if="text_credits && email_credits">and</span> <span v-if="email_credits">{{ email_credits * 1000 }} Email Credits</span><br/>
                            You will immediately be billed <strong>${{ creditCost }}</strong>
                        </div>

                        <button :disabled="saving" class="btn btn-sm btn-success mt-4" v-on:click="purchaseCredits()">Purchase Credits</button>
                    </b-card>


                    <b-card class="mb-4" title="Next Billing Summary">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Unit Price</th>
                                    <th>Quantity</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Standard User Licenses</td>
                                    <td>${{ pricePerUser }}</td>
                                    <td>
                                        <template v-if="planHasCounts">{{ account.prepaid_nbr_users }}</template>
                                        <span v-else-if="billableUsers">{{ billableUsers.full_price_users }}</span>
                                    </td>
                                    <td>
                                        <template v-if="planHasCounts">${{ pricePerUser * account.prepaid_nbr_users }}</template>
                                        <span v-else-if="billableUsers">${{ pricePerUser * billableUsers.full_price_users }}</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Free User Licenses</td>
                                    <td>$0.00</td>
                                    <td><span v-if="billableUsers">{{ billableUsers.free_users }}</span></td>
                                    <td>$0.00</td>
                                </tr>
                                <tr v-if="discount && discount.coupon">
                                    <td>
                                        Discount Coupon - {{ discount.coupon.name }}
                                        <span v-if="discount.coupon.percent_off">({{ discount.coupon.percent_off }}% Off)</span>
                                        <span v-else-if="discount.coupon.amount_off">({{ discount.coupon.amount_off / 100 | currency }} Off)</span>
                                    </td>
                                    <td>${{ discountAmount }}</td>
                                    <td>1</td>
                                    <td>${{ discountAmount }}</td>
                                </tr>
                                <tr>
                                    <td colspan="3">Total MeritEHS Licenses</td>
                                    <td class="alert-success">
                                        <strong v-if="planHasCounts">{{ (pricePerUser * account.prepaid_nbr_users - discountAmount) | currency }}</strong>
                                        <strong v-else-if="billableUsers">{{ (pricePerUser * billableUsers.full_price_users - discountAmount) | currency }}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </b-card>

                    <b-card>
                        <h3>Cancel Account</h3>
                        <p>Cancel your EHS account subscription. If you are not on a free trial, this will immediately bill you for your last prorated invoice from the last billing date to now.</p>
                        <p>All users will be logged deactivated and unable to use EHS.</p>
                        <b-button variant="danger" size="sm" v-on:click="cancelAccount()" :disabled="saving">
                            <template v-if="saving">Cancelling <i class="fa fa-spinner fa-spin"></i></template>
                            <template v-else>Cancel Account</template>
                        </b-button>
                    </b-card>
                </div>
            </div>
        </div>
        <div v-show="curTab === 'invoices'">
            <div class="card mb-3">
                <div class="card-header">Invoices</div>
                <div class="card-body">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Total</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody v-if="invoices !== null && invoices.length > 0">
                            <tr v-for="invoice in invoices">
                                <td>{{ invoice.date }}</td>
                                <td>${{ (invoice.total / 100).toFixed(2) }}</td>
                                <td>
                                    <span class="d-block mt-1" v-for="desc in invoice.description">{{ desc }}</span>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr>
                                <td colspan="3">-- No Past Invoices --</td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="invoices_has_more && lastInvoice">
                        <button class="btn btn-sm btn-info" @click="getHistory(lastInvoice.id, true, false)" :disabled="invoices_loading">Load More Invoices <span v-show="invoices_loading"><i class="fa fa-circle-o-notch fa-spin"></i></span></button>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header">Charges</div>
                <div class="card-body">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Total</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody v-if="charges !== null && charges.length > 0">
                            <tr v-for="charge in charges">
                                <td>{{ charge.date }}</td>
                                <td>{{ charge.amount | currency }}</td>
                                <td>{{ charge.description }}</td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr>
                                <td colspan="3">-- No Past Charges --</td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="charges_has_more && lastCharge">
                        <button class="btn btn-sm btn-info" @click="getHistory(lastCharge.id, false, true)" :disabled="charges_loading">Load More Charges <span v-show="charges_loading"><i class="fa fa-circle-o-notch fa-spin"></i></span></button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data() {
            return {
                loading: true,
                invoices_loading: false,
                charges_loading: false,
                saving: false,
                curTab: 'billing',
                calculatingProratedCost: false,
                stripe: null,
                elements: null,
                card: null,
                activeUsers: {},
                billableUsers: {},
                account: null,
                stripeToken: null,
                pricePerUser: null,
                pricePerUserTier2: null,
                currentstandardCount: null,
                currentActionCount: null,
                proratedCost: null,
                text_credits: null,
                email_credits: null,
                cost_per_1000_texts: null,
                cost_per_1000_emails: null,
                account: {},
                errors: {},
                discount: null,
                invoices: [],
                invoices_has_more: false,
                charges: [],
                charges_has_more: false,
                redirect_to_setup: this.$route.query && this.$route.query.setup ? true : false,
            }
        },

        watch: {
            'account.prepaid_nbr_users'(newVal, oldVal){
                //Check it
                if(newVal > oldVal)
                {
                    this.getProratedCost();
                }
            },

            'account.prepaid_nbr_action_users'(newVal, oldVal){
                //Check it
                if(newVal > oldVal)
                {
                    this.getProratedCost();
                }
            },
        },

        computed: {
            planHasCounts(){
                //Send
                return this.account.billing_plan_id != 1;
            },

            countsChanged(){
                //Check it
                if(this.currentstandardCount != this.account.prepaid_nbr_users || this.currentActionCount != this.account.prepaid_nbr_action_users)
                {
                    //Set
                    return true;
                }

                //Send it
                return false;
            },

            creditCost(){
                //SEtup
                let cost = 0.00;

                //Texts
                if(this.text_credits)
                {
                    cost += this.text_credits * this.cost_per_1000_texts;
                }

                //Texts
                if(this.email_credits)
                {
                    cost += this.email_credits * this.cost_per_1000_emails;
                }

                //Send it
                return cost;
            },

            proratedCost_OLD(){
                //Setup
                var now = moment();
                var end = now.clone().endOf('month');

                //Get the difference in days
                var daysActive = end.diff(now, 'days');
                var totalDays = now.daysInMonth();

                //Set the charge
                var prorationRate = daysActive / totalDays;
                var amount = 0;

                //Check Users
                if(parseInt(this.account.prepaid_nbr_users) > 0)
                {
                    amount += (this.pricePerUser) * (parseInt(this.account.prepaid_nbr_users) - parseInt(this.currentstandardCount)) * prorationRate;
                }

                //Action Users
                if(parseInt(this.account.prepaid_nbr_action_users) > 0)
                {
                    amount += (this.pricePerUserTier2) * (parseInt(this.account.prepaid_nbr_action_users) - parseInt(this.currentActionCount)) * prorationRate;
                }

                //SEnd it
                return amount.toFixed(2);
            },

            totalbill(){
                //Setup
                var amount = 0;

                //Check Users
                if(parseInt(this.account.prepaid_nbr_users) > 0)
                {
                    amount += (this.pricePerUser) * parseInt(this.account.prepaid_nbr_users);
                }

                //Action Users
                if(parseInt(this.account.prepaid_nbr_action_users) > 0)
                {
                    amount += (this.pricePerUserTier2) * parseInt(this.account.prepaid_nbr_action_users);
                }

                //SEnd it
                return amount.toFixed(2);
            },

            discountAmount(){
                //Check for a fixed amount off
                if(this.discount === null)
                {
                    return 0.00;
                }

                //Check for fixed amount off
                if(this.discount.coupon.amount_off)
                {
                    return this.discount.coupon.amount_off / 100;
                }

                //Send it
                return (this.totalbill * (this.discount.coupon.percent_off / 100)).toFixed(2);
            },

            lastInvoice(){
                //Check for empty
                if(this.invoices.length === 0)
                {
                    return null;
                }

                return this.invoices[this.invoices.length - 1];
            },

            lastCharge(){
                //Check for empty
                if(this.charges.length === 0)
                {
                    return null;
                }

                return this.charges[this.charges.length - 1];
            },

            getTabTitle(){
                //Setup
                var title = '';
                var icon = 'bars';

                //Switch
                switch(this.curTab)
                {
                    case 'billing':
                        //Set
                        title = 'Billing Settings';
                        break;
                    case 'invoices':
                        //Set
                        title = 'Past Invoices & Charges';
                        break;
                }

                //Send it
                return '<i class="fa fa-'+icon+'"></i> ' + title;
            }
        },

        methods: {

            updateCard(){
                //Saving
                this.saving = true;

                //Create the TOKEN
                this.stripe.createToken(this.card).then(result => {
                    //Check for an error
                    if(result.error)
                    {
                        // Inform the customer that there was an error.
                        var errorElement = document.getElementById('card-errors');
                        errorElement.textContent = result.error.message;

                        //Reset
                        this.saving = false;
                    }
                    else
                    {
                        //Set the token
                        this.stripeToken = result.token;

                        // Send the token to your server.
                        this.saveBilling();
                    }
                });
            },

            saveBilling(){
                //setup
                this.saving = true;

                //Send it
                this.$http.post('/billing', {stripeToken: this.stripeToken, billing_email: this.account.billing_email})
                    .then(response => {
                        //Alert
                        this.$toastr.success('Billing Updated');

                        //Set the values
                        this.account.card_last_four = this.stripeToken.card.last4;
                        this.account.card_brand = this.stripeToken.card.brand;

                        //Reset the card?
                        this.card.clear();

                        //Send to home
                        this.$router.push((this.redirect_to_setup ? '/admin/setup-wizard' : '/'));
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.error(error.response.data, 'Failed to Update Billing');
                        }
                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            },

            getProratedCost(){
                //setup
                this.calculatingProratedCost = true;

                //Send it
                this.$http.get('/billing/calc-prorated-cost', {prepaid_nbr_users: this.account.prepaid_nbr_users, prepaid_nbr_action_users: this.account.prepaid_nbr_action_users})
                    .then(response => {
                        //Set the value
                        if(response.data && response.data > 0)
                        {
                            this.proratedCost = response.data.toFixed(2);
                        }
                        else
                        {
                            this.proratedCost = null;
                        }

                        //Reset
                        this.calculatingProratedCost = false;
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.error(error.response.data, 'Failed to Calculate Prorated Cost');
                        }

                        //Reset
                        this.calculatingProratedCost = false;
                    })
                    .then(() => {

                    });
            },

            saveLicenses(){
                //Fix parsing
                this.saving = true;

                //Fix parsing
                this.account.prepaid_nbr_users = parseInt(this.account.prepaid_nbr_users);
                this.account.prepaid_nbr_action_users = parseInt(this.account.prepaid_nbr_action_users);

                //Send it
                this.$http.post('/billing/licenses', this.account)
                    .then(response => {
                        //Alert
                        this.$toastr.success('User Licenses Updated');

                        //Set the values
                        this.currentstandardCount = this.account.prepaid_nbr_users;
                        this.currentActionCount = this.account.prepaid_nbr_action_users;
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.error(error.response.data, 'Failed to Update User Licenses');
                        }
                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            },

            purchaseCredits(){
                //Fix parsing
                this.saving = true;

                //Send it
                this.$http.post('/billing/sending-credits', {text_credits: this.text_credits, email_credits: this.email_credits})
                    .then(response => {
                        //Alert
                        this.$toastr.success('Credits Purchased');

                        //Set the values
                        this.text_credits = null;
                        this.email_credits = null;

                        //Set the Account Limits
                        this.account.monthly_text_limit = response.data.monthly_text_limit;
                        this.account.monthly_email_limit = response.data.monthly_email_limit;
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.error(error.response.data, 'Failed to Purchase Sending Credits');
                        }
                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            },

            cancelAccount(){
                //Confirm
                if(!confirm('Are you sure you want to cancel your account?'))
                {
                    return;
                }

                //Saving
                this.saving = true;

                //Send it
                this.$http.post('/billing/cancel')
                    .then(resposne => {
                        //Alert
                        this.$toastr.warning('Account Cancelled');

                        //Send to login
                        window.location = '/Logout';
                    })
                    .catch(err => {

                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            },

            //Fetch from the server
            loadStripeResources(){
                //Get it
                this.$http.get('/billing')
                    .then(response => {
                        //Done loading
                        this.loading = false;

                        //Set the variables
                        this.activeUsers = response.data.activeUsers;
                        this.billableUsers = response.data.billableUsers;
                        this.account = response.data.account;
                        this.pricePerUser = response.data.pricePerUser;
                        this.pricePerUserTier2 = response.data.pricePerUserTier2;
                        this.currentstandardCount = this.account.prepaid_nbr_users;
                        this.currentActionCount = this.account.prepaid_nbr_action_users;
                        this.cost_per_1000_texts = response.data.cost_per_1000_texts;
                        this.cost_per_1000_emails = response.data.cost_per_1000_emails;
                        this.discount = response.data.discount;

                        //Setup Stripe Elements
                        this.stripe = Stripe(response.data.publicKey);
                        this.elements = this.stripe.elements();

                        //Create the card
                        this.card = this.elements.create('card', {style: {base: {fontSize: '16px', color: '#32325d'}}});

                        //Mount it
                        this.card.mount('#card-element');

                        //Add the event listener
                        this.card.addEventListener('change', ({error}) => {
                            //Display the errors
                            const displayError = document.getElementById('card-errors');

                            //Check for an error
                            if (error)
                            {
                                displayError.textContent = error.message;
                            }
                            else
                            {
                                displayError.textContent = '';
                            }
                        });

                        //Get the invoices
                        this.getHistory();
                    })
                    .catch(err => {
                        //Log it
                        console.log(err);
                    })
                    .then(() => {
                        //Done loading
                        this.loading = false;
                    });
            },

            includeStripe( URL, callback ){
                let documentTag = document, tag = 'script',
                    object = documentTag.createElement(tag),
                    scriptTag = documentTag.getElementsByTagName(tag)[0];

                //Set SRC
                object.src = '//' + URL;

                //Set Callback
                if (callback)
                {
                    object.addEventListener('load', function (e) { callback(null, e); }, false);
                }

                //Setup
                scriptTag.parentNode.insertBefore(object, scriptTag);
            },

            getHistory(last_id = null, load_invoices = true, load_charges = true){
                //Loading
                this.invoices_loading = load_invoices;
                this.charges_loading = load_charges;

                //Setup payload
                var payload = {
                    load_invoices: load_invoices,
                    load_charges: load_charges,
                };

                //Check for it
                if(last_id)
                {
                    payload['starting_after'] = last_id;
                }

                //Get it
                this.$http.get('/billing/invoices', payload)
                    .then(response => {
                        //Set the variables
                        if(load_invoices)
                        {
                            this.invoices.push(...response.data.invoices);
                            this.invoices_has_more = response.data.invoices_has_more;
                        }

                        //Check charges
                        if(load_charges)
                        {
                            this.charges.push(...response.data.charges);
                            this.charges_has_more = response.data.charges_has_more;
                        }
                    })
                    .catch(err => {
                        //Log it
                        console.log(err);
                    })
                    .then(() => {
                        //Done loading
                        this.invoices_loading = false;
                        this.charges_loading = false;
                    });
            },
        },

        mounted(){
            //Setup the NEXT
            this.$nextTick(function () {
                //Setup Stripe
                this.includeStripe('js.stripe.com/v3/', function(){
                    //Load the respources
                    this.loadStripeResources();
                }.bind(this));
            });
        }
    }
</script>
